import { useEffect, useMemo, useState } from "react";
import { RelayPool } from "applesauce-relay";
import { toArray, lastValueFrom } from "rxjs";
import {
  parseNsiteHostname,
  getFallbackNsiteContext,
  buildLatestSiteUrl,
  DEFAULT_NSITE_GATEWAY,
  type NsiteContext,
} from "@/lib/nsite";

/** Minimal shape of a Nostr event for our purposes. */
export interface NostrEvent {
  id: string;
  pubkey: string;
  created_at: number;
  kind: number;
  tags: string[][];
  content: string;
  sig: string;
}

/** Relays to query for snapshot events. */
const SNAPSHOT_RELAYS = [
  "wss://relay.damus.io",
  "wss://relay.primal.net",
  "wss://relay.ditto.pub",
  "wss://nos.lol",
];

/** Extract the first matching tag value. */
function getTag(tags: string[][], name: string): string | undefined {
  return tags.find(([t]) => t === name)?.[1];
}

/**
 * Derive the `a` tag value that snapshot events reference, given a context.
 * Returns `null` for snapshot contexts (those need a two-step resolve).
 */
function aTagFromContext(ctx: NsiteContext): string | null {
  if (!ctx) return null;
  switch (ctx.type) {
    case "root":
      return `15128:${ctx.pubkey}:`;
    case "named":
      return `35128:${ctx.pubkey}:${ctx.dTag}`;
    case "snapshot":
      return null; // handled separately
  }
}

export interface NsiteSnapshotsResult {
  /** All snapshot events for this site, newest first. */
  snapshots: NostrEvent[];
  /** The parsed nsite context (always non-null thanks to fallback). */
  context: NonNullable<NsiteContext>;
  /** If we're currently viewing a snapshot, its event id. */
  currentSnapshotId: string | null;
  /** URL to the latest (live) version of the site, if resolvable. */
  latestUrl: string | null;
  /** The `a` tag value referencing the parent nsite manifest. */
  parentATag: string | null;
  /** Whether we're still loading. */
  loading: boolean;
  /** Error message, if any. */
  error: string | null;
}

/**
 * Fetch all kind 5128 manifest snapshot events for the current nsite.
 *
 * Works in three modes depending on hostname:
 *
 * 1. **Named / root site**: Directly builds the `a` tag filter and fetches snapshots.
 * 2. **Snapshot subdomain**: First fetches the snapshot event by ID, reads its
 *    `a` tag to discover the parent nsite, then fetches all sibling snapshots.
 * 3. **Non-nsite hostname** (localhost, shakespeare.to, etc.): Falls back to the
 *    hardcoded site identity and behaves like mode 1.
 */
export function useNsiteSnapshots(pool: RelayPool): NsiteSnapshotsResult {
  const context = useMemo<NonNullable<NsiteContext>>(
    () => parseNsiteHostname(window.location.hostname) ?? getFallbackNsiteContext(),
    [],
  );

  const [snapshots, setSnapshots] = useState<NostrEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // For snapshot contexts, we resolve the parent `a` tag asynchronously.
  const [resolvedATag, setResolvedATag] = useState<string | null>(null);

  // Directly derivable `a` tag (for non-snapshot contexts).
  const directATag = useMemo(() => aTagFromContext(context), [context]);

  const currentSnapshotId = context.type === "snapshot" ? context.snapshotId : null;

  // The effective `a` tag — either directly derived or resolved from the snapshot.
  const aTagValue = directATag ?? resolvedATag;

  // Gateway for building URLs.
  const gateway = context.gateway ?? DEFAULT_NSITE_GATEWAY;
  const protocol = typeof window !== "undefined" ? window.location.protocol : "https:";

  // Step 1 (snapshot only): fetch the snapshot event to discover the parent `a` tag.
  useEffect(() => {
    if (context.type !== "snapshot") return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const events = await lastValueFrom(
          pool
            .request(SNAPSHOT_RELAYS, { ids: [context.snapshotId], limit: 1 })
            .pipe(toArray()),
        );
        if (cancelled) return;

        const snap = (events as NostrEvent[])[0];
        if (!snap) {
          setError("Snapshot event not found");
          setLoading(false);
          return;
        }

        const aTag = getTag(snap.tags, "a");
        if (!aTag) {
          setError("Snapshot has no parent reference");
          setLoading(false);
          return;
        }

        setResolvedATag(aTag);
        // Loading continues — step 2 will fire when aTagValue changes.
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to fetch snapshot");
          setLoading(false);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [pool, context]);

  // Step 2: fetch all sibling snapshots using the `a` tag.
  useEffect(() => {
    if (!aTagValue) return;
    let cancelled = false;
    setLoading(true);
    // Don't clear error from step 1 here — only set new ones.

    (async () => {
      try {
        const events = await lastValueFrom(
          pool
            .request(SNAPSHOT_RELAYS, { kinds: [5128], "#a": [aTagValue], limit: 50 })
            .pipe(toArray()),
        );

        if (cancelled) return;

        // Deduplicate by event id and sort newest first
        const seen = new Set<string>();
        const unique: NostrEvent[] = [];
        for (const ev of events as NostrEvent[]) {
          if (!seen.has(ev.id)) {
            seen.add(ev.id);
            unique.push(ev);
          }
        }
        unique.sort((a, b) => b.created_at - a.created_at);
        setSnapshots(unique);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to fetch snapshots");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [pool, aTagValue]);

  // Build the "latest" URL from the `a` tag.
  const latestUrl = useMemo(() => {
    if (!aTagValue) return null;
    return buildLatestSiteUrl(aTagValue, gateway, protocol);
  }, [aTagValue, gateway, protocol]);

  return {
    snapshots,
    context,
    currentSnapshotId,
    latestUrl,
    parentATag: aTagValue,
    loading,
    error,
  };
}
